import { useMemo } from 'react';
import { Polyline } from 'react-leaflet';

// Function to get color based on altitude (0m to 13000m)
// Orange below 6000m, red above 6000m
const getAltitudeColor = (altitude) => {
  const maxAltitude = 13000; // meters
  const transitionAltitude = 6000; // meters - transition point from orange to red

  let r, g, b;

  if (altitude <= transitionAltitude) {
    // 0m to 6000m: Light yellow-orange to bright orange
    // Normalize to 0-1 within this range
    const normalized = Math.min(altitude / transitionAltitude, 1);

    // Light yellow-orange: rgb(255, 200, 50) to bright orange: rgb(255, 140, 0)
    r = 255;
    g = Math.round(200 - (60 * normalized)); // 200 to 140
    b = Math.round(50 * (1 - normalized)); // 50 to 0
  } else {
    // 6000m to 13000m: Red to very dark red
    // Normalize to 0-1 within this range
    const normalized = Math.min((altitude - transitionAltitude) / (maxAltitude - transitionAltitude), 1);

    // Red: rgb(220, 50, 0) to very dark red: rgb(80, 0, 0)
    r = Math.round(220 - (140 * normalized)); // 220 to 80
    g = Math.round(50 * (1 - normalized)); // 50 to 0
    b = 0;
  }

  return `rgb(${r}, ${g}, ${b})`;
};

function AircraftTrack({ trackData, selectedAircraftHex = null, liveAircraftData = {} }) {
  // Combine historical track points with live positions
  const trackSegments = useMemo(() => {
    // Verbose logging disabled
    // console.log('🎨 AircraftTrack rendering:', {
    //   hasTrackData: !!trackData,
    //   pointCount: trackData?.points?.length || 0,
    //   selectedHex: selectedAircraftHex,
    //   trackData: trackData
    // });

    if (!trackData || !trackData.points || trackData.points.length === 0) {
      // console.log('⚠️ No track data to display');
      return [];
    }

    const segments = [];
    const points = [...trackData.points];

    // console.log('📍 First point:', points[0]);
    // console.log('📍 Last point:', points[points.length - 1]);

    // Convert altitudes from feet to meters for color calculation
    // New API returns altitudeFeet field
    const altitudes = points.map(p => {
      const altFeet = p.altitudeFeet || p.altitude || 0;
      return Math.round(altFeet * 0.3048); // Convert feet to meters
    });
    // console.log('📊 Altitude range (meters):', `${Math.min(...altitudes)}m to ${Math.max(...altitudes)}m`);
    // console.log('📊 Sample altitudes (first 5, meters):', altitudes.slice(0, 5));

    // Add live position if aircraft is selected and has new position
    if (selectedAircraftHex && liveAircraftData[selectedAircraftHex]) {
      const liveAircraft = liveAircraftData[selectedAircraftHex];

      // Check if live position is newer than last track point
      const lastTrackPoint = points[points.length - 1];
      const lastTrackTime = new Date(lastTrackPoint.timestamp).getTime();
      const liveTime = liveAircraft.lastSeen || Date.now();

      // Only add if it's been at least 5 seconds since last track point
      if (liveTime - lastTrackTime >= 5000 && liveAircraft.lat && liveAircraft.lon) {
        const livePoint = {
          latitude: liveAircraft.lat,
          longitude: liveAircraft.lon,
          altitudeFeet: liveAircraft.alt_baro || 0,
          timestamp: new Date(liveTime).toISOString()
        };
        points.push(livePoint);
        // Add the live altitude in meters to our altitudes array
        altitudes.push(Math.round((liveAircraft.alt_baro || 0) * 0.3048));
      }
    }

    // Create polyline segments with colors based on altitude
    for (let i = 0; i < points.length - 1; i++) {
      const point1 = points[i];
      const point2 = points[i + 1];

      // Get altitude in meters from our converted array
      const alt1Meters = altitudes[i];
      const alt2Meters = altitudes[i + 1];

      // Use average altitude for the segment color
      const avgAltitude = (alt1Meters + alt2Meters) / 2;
      const color = getAltitudeColor(avgAltitude);

      // Log first few segments to see color calculation (disabled)
      // if (i < 3 || i === points.length - 2) {
      //   console.log(`Segment ${i}: alt1=${alt1Meters}m, alt2=${alt2Meters}m, avg=${avgAltitude}m, color=${color}`);
      // }

      segments.push({
        positions: [
          [point1.latitude, point1.longitude],
          [point2.latitude, point2.longitude]
        ],
        color: color,
        altitude: avgAltitude
      });
    }

    // console.log(`✅ Created ${segments.length} track segments`);
    // if (segments.length > 0) {
    //   console.log('🎨 First segment:', {
    //     positions: segments[0].positions,
    //     color: segments[0].color,
    //     altitude: segments[0].altitude
    //   });
    //   console.log('🎨 Last segment:', {
    //     positions: segments[segments.length - 1].positions,
    //     color: segments[segments.length - 1].color,
    //     altitude: segments[segments.length - 1].altitude
    //   });
    // }

    return segments;
  }, [trackData, selectedAircraftHex, liveAircraftData]);

  // console.log('🖼️ AircraftTrack render - segments:', trackSegments.length);

  if (trackSegments.length === 0) {
    // console.log('⚠️ No segments to render');
    return null;
  }

  // console.log(`🎨 Rendering ${trackSegments.length} Polyline components`);

  return (
    <>
      {trackSegments.map((segment, index) => {
        // if (index === 0) {
        //   console.log('🎨 Rendering first polyline:', {
        //     key: `track-segment-${index}`,
        //     positions: segment.positions,
        //     color: segment.color
        //   });
        // }
        return (
          <Polyline
            key={`track-segment-${index}`}
            positions={segment.positions}
            pathOptions={{
              color: segment.color,
              weight: 2,
              opacity: 0.9
            }}
          />
        );
      })}
    </>
  );
}

export default AircraftTrack;
